'use client';

import { useHeroToolForm } from '../../../hooks/use-hero-tool-form';
import type { HeroSectionData } from '../../../types';
import { PortableTextContent } from '../../PortableTextContent';
import { StickyToolInputBar } from '../../StickyToolInputBar';
import { HeroBackground } from './components/HeroBackground';
import { HeroInputForm } from './components/HeroInputForm';
import { PresetPromptChips } from './components/PresetPromptChips';

export function ImmersiveHero({ section }: { section: HeroSectionData }) {
  const toolType = section.toolType ?? null;
  const buttonHref = section.button?.href;

  const {
    textPrompt,
    setTextPrompt,
    referenceImages,
    handlePresetClick,
    handleCreate,
    handleImageUpload,
    handleRemoveImage,
  } = useHeroToolForm(toolType, buttonHref);

  return (
    <>
      <section className="relative h-screen w-full overflow-hidden">
        <HeroBackground media={section.showcaseMedia} />

        <div className="relative z-10 flex h-full items-center sm:justify-center">
          <div className="ml-[4.4%] flex w-[577px] flex-col gap-8 sm:w-full sm:max-w-[480px]">
            {/* Badge */}
            <div className="inline-flex w-fit items-center gap-2.5 rounded-full border border-white/[0.08] bg-white/[0.06] px-3 py-[6px]">
              <span className="size-1.5 animate-pulse rounded-full bg-[#05df72] shadow-[0_0_6px_rgba(74,222,128,0.8)]" />
              <span className="font-medium text-[11px] text-white/60 leading-[16.5px] tracking-[0.065px]">
                Trusted by 2M+ creators worldwide
              </span>
            </div>

            {/* Title + Description */}
            <div className="flex flex-col gap-2">
              <h1 className="font-semibold text-[60px] text-white leading-[1.08] sm:text-[36px]">{section.title}</h1>
              {section.description && (
                <PortableTextContent
                  className="text-base leading-[26px] tracking-[-0.3px] [&_p]:text-white/40"
                  value={section.description}
                />
              )}
            </div>

            {/* Input form + Presets */}
            {toolType && (
              <div className="flex flex-col gap-4">
                <HeroInputForm
                  textPrompt={textPrompt}
                  referenceImages={referenceImages}
                  toolType={toolType}
                  onChangePrompt={setTextPrompt}
                  onCreate={handleCreate}
                  onImageUpload={handleImageUpload}
                  onRemoveImage={handleRemoveImage}
                />

                {!!section.presetPrompts?.length && (
                  <PresetPromptChips onClickPreset={handlePresetClick} presets={section.presetPrompts} variant="oval" />
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {toolType && (
        <StickyToolInputBar
          toolType={toolType}
          textPrompt={textPrompt}
          referenceImages={referenceImages}
          onChangePrompt={setTextPrompt}
          onCreate={handleCreate}
          onImageUpload={handleImageUpload}
          onRemoveImage={handleRemoveImage}
        />
      )}
    </>
  );
}
